import { App, Button, Select, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import type { InterventionFilePdfTemplate } from '../../../../services/interventionFile/InterventionFileService';

const saveBlobCrossPlatform = (blob: Blob, filename: string) => {
  const w = window as any;
  if (w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function') {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        w.AndroidInterface.saveBlobFile(base64, filename);
      } catch {}
    };
    reader.readAsDataURL(blob);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const sanitizeFilename = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildPdfFilename = (record: any, fallbackId: string) => {
  const companyName = record?.company?.name ?? '';
  const workPlaceName = record?.workPlace?.name ?? '';
  const fileNumber = record?.number ?? '';
  const base =
    !companyName || !workPlaceName || !fileNumber ? fallbackId : `${companyName} ${workPlaceName} ${fileNumber}`;
  return `${sanitizeFilename(base)}.pdf`;
};

const InterventionFilePreview: React.FC = () => {
  const { id } = useParams();
  const { message } = App.useApp();
  const [record, setRecord] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState<InterventionFilePdfTemplate>('auto');

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    InterventionFileService.search({
      ids: [id],
      isCompanyIncluded: true,
      isWorkPlaceIncluded: true,
    })
      .then((data: any) => {
        setRecord(data?.response?.[0] ?? null);
      })
      .catch((error: any) => {
        console.error('Error loading intervention file details:', error);
        message.error('Nu am putut încărca detaliile fișei de intervenție.');
      })
      .finally(() => setIsLoading(false));
  }, [id, message]);

  const iframeSrc = useMemo(
    () => (id ? InterventionFileService.getPrintUrl(id, selectedPdfTemplate) : ''),
    [id, selectedPdfTemplate],
  );

  const handleDownload = async () => {
    if (!id) return;
    try {
      setIsDownloading(true);
      const response = await InterventionFileService.print(id, selectedPdfTemplate);
      const blob = await response.blob();
      const filename = buildPdfFilename(record, id);
      saveBlobCrossPlatform(blob, filename);
    } catch (error) {
      console.error('Error downloading intervention file:', error);
      message.error('A apărut o eroare la descărcarea fișei de intervenție.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: '#3C3C3C',
          color: '#fff',
        }}
      >
        <div>Intervention File</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select
            value={selectedPdfTemplate}
            style={{ width: 220 }}
            onChange={(value) => setSelectedPdfTemplate(value)}
            options={[
              { value: 'auto', label: 'Template PDF: Automat' },
              { value: 'helion', label: 'Template PDF: Helion' },
              { value: 'cxc', label: 'Template PDF: CXC' },
            ]}
          />
          <Button type='primary' onClick={handleDownload} loading={isDownloading}>
            Download
          </Button>
        </div>
      </div>
      <div style={{ flex: 1, background: '#f0f2f5' }}>
        {isLoading ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : (
          <iframe title='Intervention File PDF' src={iframeSrc} style={{ width: '100%', height: '100%', border: 0 }} />
        )}
      </div>
    </div>
  );
};

export default InterventionFilePreview;
